import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  useToast,
  IconButton,
  useBreakpointValue,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Select,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Checkbox,
  SimpleGrid,
} from '@chakra-ui/react'
import {
  MagnifyingGlass,
  Hourglass,
  Play,
  ChatTeardropDots,
  CaretDown,
  EnvelopeSimple,
  ArrowsLeftRight,
  CheckSquare,
  Funnel,
  List,
} from '@phosphor-icons/react'
import { formatDistanceToNow, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import api from '../../services/api'
import { useSocket } from '../../contexts/SocketContext'
import { useAuth } from '../../contexts/AuthContext'
import socketService, { SOCKET_EVENTS } from '../../services/socket'
import Chat from './Chat'
import UserInfoSidebar from './UserInfoSidebar'
import { WhatsappBadge, formatWaId, isPlaceholderGuestEmail } from '../../components/WhatsappBadge'
import { GuestBadge } from './GuestInfoPanel'
import { Sidebar } from '../../layouts/DefaultLayout'
import AccessTags, { type AccessTag } from '../../components/AccessTags'
import { onResync, dispararResync } from '../../utils/resync'
import { usePullToRefresh, PullToRefreshIndicator } from '../../hooks/usePullToRefresh'

interface SupportChat {
  id: number
  status: string
  userId: number
  ownerName: string
  ownerEmail: string
  ownerProfilePic: string | null
  /** Planos e acessos do vendedor (catálogo do admin). */
  ownerTags?: AccessTag[]
  categoryName: string
  categoryColor: string | null
  customStatusName: string | null
  customStatusColor: string | null
  unreadCountAgent: number
  createdAt: string
  updatedAt: string
  lastUserMessageAt: string | null
  lastMessage?: {
    message: string
    createdAt: string
    senderType: string
  }
  transferredAt?: string | null
  lastTransferDate?: string | null
  transferredFromName?: string | null
  /** Concluído e reaberto pelo próprio cliente (volta à frente da fila). */
  isReopened?: boolean | number
  /** Visitante da tela de login (support_chats.guestSessionId). O dono é um usuário-sombra. */
  isGuest?: boolean
  guestEmail?: string | null
  guestName?: string | null
  channel?: 'web' | 'whatsapp'
  whatsappWaId?: string | null
  whatsappProfileName?: string | null
  accountStatus?: string | null
  diagnosisCode?: string | null
  /** Conta real já vinculada pelo atendente. */
  guestLinkedUserId?: number | null
}

function ChatItem({
  chat,
  isSelected,
  onClick,
  showMenu = false,
  onMarkUnread,
  onTransfer,
  onFinish,
  isTyping = false,
}: {
  chat: SupportChat
  isSelected: boolean
  onClick: () => void
  showMenu?: boolean
  onMarkUnread?: (chatId: number) => void
  onTransfer?: (chatId: number) => void
  onFinish?: (chatId: number) => void
  isTyping?: boolean
}) {
  const hasUnread = chat.unreadCountAgent > 0

  const bgSelected = useColorModeValue('gray.100', 'gray.700')
  const bgHoverDefault = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const textMutedSame = useColorModeValue('gray.500', 'gray.500')
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const surfaceHover = useColorModeValue('gray.100', 'gray.700')
  const bgHoverButton = useColorModeValue('gray.200', 'gray.600')
  const badgeBgFallback = useColorModeValue('gray.300', 'gray.600')
  const avatarTextOnYellow = useColorModeValue('gray.800', 'gray.900')

  const getBgColor = () => {
    if (isSelected) return bgSelected
    if (hasUnread) return 'yellow.900' // Fundo destacado para não lidas
    return 'transparent'
  }

  const getHoverBgColor = () => {
    if (isSelected) return bgSelected
    if (hasUnread) return 'yellow.800'
    return bgHoverDefault
  }

  return (
    <Box
      px={{ base: 3, md: 3 }}
      py={{ base: 3, md: 3 }}
      borderRadius="lg"
      bg={getBgColor()}
      _hover={{ bg: getHoverBgColor() }}
      cursor="pointer"
      onClick={onClick}
      position="relative"
      borderLeft={isSelected ? '3px solid' : hasUnread ? '3px solid' : '3px solid transparent'}
      borderLeftColor={isSelected ? 'yellow.500' : hasUnread ? 'yellow.400' : 'transparent'}
      borderBottom="1px solid"
      borderBottomColor={borderSubtle}
      flexShrink={0}
      transition="background-color 0.2s ease"
    >
      <HStack spacing={{ base: 2, md: 3 }} align="start">
        <Avatar
          size={{ base: 'md', lg: 'sm' }}
          name={chat.ownerName}
          src={chat.ownerProfilePic || undefined}
          bg="yellow.500"
          color={avatarTextOnYellow}
          flexShrink={0}
        />
        <VStack flex={1} align="start" spacing={0} overflow="hidden" minW={0}>
          <HStack w="100%" justify="space-between" align="start" spacing={2}>
            <Text fontWeight="semibold" color={textPrimary} noOfLines={1} fontSize={{ base: 'md', lg: 'sm' }} flex={1} minW={0}>
              {chat.ownerName}
            </Text>
            <HStack spacing={1} flexShrink={0}>
              {chat.unreadCountAgent > 0 && (
                <Badge
                  colorScheme="red"
                  borderRadius="full"
                  fontSize="10px"
                  minW={4}
                  textAlign="center"
                >
                  {chat.unreadCountAgent > 99 ? '99+' : chat.unreadCountAgent}
                </Badge>
              )}
              {showMenu && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Opções"
                    icon={<CaretDown size={14} />}
                    variant="ghost"
                    size="xs"
                    color={textMuted}
                    _hover={{ color: textPrimary, bg: bgHoverButton }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <MenuList bg={surfaceBg} borderColor={borderSubtle} minW="180px">
                    <MenuItem
                      icon={<EnvelopeSimple size={16} />}
                      bg={surfaceBg}
                      _hover={{ bg: surfaceHover }}
                      onClick={(e) => {
                        e.stopPropagation()
                        onMarkUnread?.(chat.id)
                      }}
                    >
                      Marcar como não lida
                    </MenuItem>
                    {chat.status !== 'completed' && (
                      <MenuItem
                        icon={<ArrowsLeftRight size={16} />}
                        bg={surfaceBg}
                        _hover={{ bg: surfaceHover }}
                        onClick={(e) => {
                          e.stopPropagation()
                          onTransfer?.(chat.id)
                        }}
                      >
                        Transferir
                      </MenuItem>
                    )}
                    {chat.status !== 'completed' && (
                      <MenuItem
                        icon={<CheckSquare size={16} />}
                        bg={surfaceBg}
                        _hover={{ bg: surfaceHover }}
                        color="green.400"
                        onClick={(e) => {
                          e.stopPropagation()
                          onFinish?.(chat.id)
                        }}
                      >
                        Concluir atendimento
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </HStack>
          <HStack spacing={1} flexWrap="wrap" mt={1} w="100%">
            <Badge
              size="sm"
              bg={chat.categoryColor || 'blue.500'}
              color={textPrimary}
              fontSize={{ base: '9px', md: '10px' }}
              borderRadius="full"
              px={1}
              noOfLines={1}
            >
              {chat.categoryName || 'Sem categoria'}
            </Badge>
            {chat.isGuest && chat.channel !== 'whatsapp' && <GuestBadge size="xs" />}
            {chat.channel === 'whatsapp' && <WhatsappBadge size="xs" />}
            {chat.customStatusName && (
              <Badge
                size="sm"
                bg={chat.customStatusColor || badgeBgFallback}
                color={textPrimary}
                fontSize={{ base: '9px', md: '10px' }}
                borderRadius="full"
                px={1}
                noOfLines={1}
              >
                {chat.customStatusName}
              </Badge>
            )}
            {!!chat.isReopened && chat.status !== 'completed' && (
              <Badge
                size="sm"
                bg="orange.500"
                color={textPrimary}
                fontSize={{ base: '9px', md: '10px' }}
                borderRadius="full"
                px={1}
                noOfLines={1}
                title="O cliente voltou a escrever num atendimento concluído; ele mantém a posição original na fila"
              >
                Reaberto
              </Badge>
            )}
          </HStack>
          {chat.ownerTags && chat.ownerTags.length > 0 && (
            <Box mt={1} w="100%">
              <AccessTags tags={chat.ownerTags} size="xs" max={3} />
            </Box>
          )}
          {isTyping ? (
            <Text fontSize={{ base: '2xs', md: 'xs' }} color="green.400" noOfLines={1} flex={1} fontStyle="italic" mt={1}>
              digitando...
            </Text>
          ) : (
            <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted} noOfLines={1} flex={1} mt={1}>
              {chat.lastMessage?.message || 'Sem mensagens'}
            </Text>
          )}
          <HStack w="100%" justify="space-between" mt={1} spacing={1} flexWrap="wrap">
            <Text fontSize="2xs" color={textMutedSame} noOfLines={1}>
              {format(new Date(chat.createdAt), 'dd/MM/yy', { locale: ptBR })}
            </Text>
            <Text fontSize="2xs" color={chat.lastTransferDate ? 'blue.300' : textMutedSame} flexShrink={0} noOfLines={1}>
              {chat.lastTransferDate
                ? format(new Date(chat.lastTransferDate), 'dd/MM/yy', { locale: ptBR })
                : formatDistanceToNow(new Date(chat.updatedAt), {
                    addSuffix: true,
                    locale: ptBR,
                  })
              }
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  )
}

const SHOW_PENDING_CHAT_LIST = import.meta.env.VITE_SHOW_PENDING_CHAT_LIST === 'true'

// Tamanho da página do scroll infinito e teto do refetch de reconciliação
// (em segundo plano, cobre as páginas já carregadas para não encolher a lista).
const PAGE_SIZE = 30
const MAX_SYNC_LIMIT = 300

/** Quantos itens o refetch de reconciliação pede: tudo que já está carregado. */
function syncLimitFor(loaded: number) {
  return Math.min(MAX_SYNC_LIMIT, Math.max(PAGE_SIZE, Math.ceil(loaded / PAGE_SIZE) * PAGE_SIZE))
}

/** Acrescenta a página nova sem duplicar ids (uma reconciliação pode ter chegado antes). */
function appendUnique(prev: SupportChat[], next: SupportChat[]) {
  const ids = new Set(prev.map((c) => c.id))
  return [...prev, ...next.filter((c) => !ids.has(c.id))]
}

/** Mesma ordem do backend (myChats): transferidos por último, não lidos primeiro, depois id asc. */
function sortMyChats(list: SupportChat[]) {
  return [...list].sort((a, b) => {
    if (a.transferredAt && !b.transferredAt) return 1
    if (!a.transferredAt && b.transferredAt) return -1
    if (a.unreadCountAgent > 0 && b.unreadCountAgent === 0) return -1
    if (a.unreadCountAgent === 0 && b.unreadCountAgent > 0) return 1
    return a.id - b.id
  })
}

function PendingChatsTab({ onStartChat }: { onStartChat?: () => void }) {
  const [pendingCount, setPendingCount] = useState(0)
  const [categories, setCategories] = useState<{ id: number; name: string; count: number }[]>([])
  const [pendingChats, setPendingChats] = useState<SupportChat[]>([])
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [selectedPlan, setSelectedPlan] = useState<string>('')
  const [tempCategories, setTempCategories] = useState<number[]>([])
  const [tempPlan, setTempPlan] = useState<string>('')
  const [modalCategories, setModalCategories] = useState<{ id: number; name: string; count: number }[]>([])
  const [isLoadingModalCategories, setIsLoadingModalCategories] = useState(false)
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure()
  const [isLoading, setIsLoading] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [startingChatId, setStartingChatId] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  const { onChatNew, onChatStarted, onChatFinished } = useSocket()

  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderSubtle = useColorModeValue('gray.200', 'gray.700')
  const borderMedium = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const textMutedSame = useColorModeValue('gray.500', 'gray.500')
  const inputBg = useColorModeValue('gray.100', 'gray.700')
  const bgHoverDefault = useColorModeValue('gray.200', 'gray.700')
  const iconMuted = useColorModeValue('gray.500', 'gray.600')
  const textOnYellow = useColorModeValue('gray.800', 'gray.900')

  // Lista atual (para o refetch de reconciliação saber quanto já está carregado)
  // e geração da consulta: troca de filtro invalida respostas em voo.
  const pendingChatsRef = useRef<SupportChat[]>([])
  useEffect(() => {
    pendingChatsRef.current = pendingChats
  }, [pendingChats])
  const listGenRef = useRef(0)

  /**
   * Sem `background`: carga inicial / troca de filtro (spinner, volta ao topo).
   * Com `background`: reconciliação após evento de socket — busca de novo as
   * páginas já carregadas e troca a lista no lugar, sem spinner. O container de
   * scroll continua montado e as keys são estáveis, então a posição de rolagem
   * é preservada.
   */
  const fetchPending = useCallback(async (opts?: { background?: boolean }) => {
    const background = !!opts?.background
    const gen = background ? listGenRef.current : ++listGenRef.current
    const limit = background ? syncLimitFor(pendingChatsRef.current.length) : PAGE_SIZE
    if (!background) {
      setIsLoading(true)
      setPage(1)
      setHasMore(true)
    }
    try {
      const response = await api.get('/chats/pending', {
        params: {
          categoryIds: selectedCategories,
          includeChats: SHOW_PENDING_CHAT_LIST ? 'true' : 'false',
          page: 1,
          limit,
          planType: selectedPlan || undefined,
        },
      })
      if (gen !== listGenRef.current) return // filtro mudou no meio do caminho
      setPendingCount(response.data.count)
      setCategories(response.data.categories)
      if (SHOW_PENDING_CHAT_LIST && response.data.chats) {
        const fresh: SupportChat[] = response.data.chats
        setPendingChats(fresh)
        setPage(Math.max(1, Math.ceil(fresh.length / PAGE_SIZE)))
        const meta = response.data.chatsMeta
        setHasMore(meta ? fresh.length < Number(meta.total) : fresh.length >= limit)
      }
    } catch (error) {
      console.error('Error fetching pending:', error)
    } finally {
      if (!background) setIsLoading(false)
    }
  }, [selectedCategories, selectedPlan])

  const fetchMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !SHOW_PENDING_CHAT_LIST) return
    setIsLoadingMore(true)
    const gen = listGenRef.current
    const nextPage = page + 1
    try {
      const response = await api.get('/chats/pending', {
        params: {
          categoryIds: selectedCategories,
          includeChats: 'true',
          page: nextPage,
          limit: PAGE_SIZE,
          planType: selectedPlan || undefined,
        },
      })
      if (gen !== listGenRef.current) return
      const newChats = response.data.chats || []
      if (newChats.length > 0) {
        setPendingChats((prev) => appendUnique(prev, newChats))
        setPage(nextPage)
      }
      const meta = response.data.chatsMeta
      if (meta && nextPage >= meta.lastPage) {
        setHasMore(false)
      }
      if (newChats.length === 0) {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching more pending chats:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [selectedCategories, selectedPlan, page, isLoadingMore, hasMore])

  useEffect(() => {
    fetchPending()
  }, [fetchPending])

  // Rajadas de eventos socket (chat:new/chat:started em pico) viravam um fetch
  // por evento em CADA agente conectado — tempestade de queries simultâneas no
  // banco. Agrupa a rajada num único refetch 2,5s após o primeiro evento.
  const fetchPendingRef = useRef(fetchPending)
  useEffect(() => {
    fetchPendingRef.current = fetchPending
  }, [fetchPending])
  const pendingRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const schedulePendingRefetch = useCallback(() => {
    if (pendingRefetchTimer.current) return // rajada em andamento — absorve
    pendingRefetchTimer.current = setTimeout(() => {
      pendingRefetchTimer.current = null
      fetchPendingRef.current({ background: true })
    }, 2500)
  }, [])
  useEffect(
    () => () => {
      if (pendingRefetchTimer.current) clearTimeout(pendingRefetchTimer.current)
    },
    []
  )

  // App voltou do segundo plano / socket reconectou: o que chegou nesse meio
  // tempo não vem por evento, então busca de novo (sem spinner).
  useEffect(
    () =>
      onResync((origem) => {
        if (origem !== 'pendentes') fetchPendingRef.current({ background: true })
      }),
    []
  )

  // Puxar para atualizar (celular): reconecta, avisa as outras telas e espera
  // a própria lista chegar para tirar o carregando.
  const pendingListRef = useRef<HTMLDivElement>(null)
  const { pull, refreshing, arrastando } = usePullToRefresh(pendingListRef, async () => {
    dispararResync('pendentes')
    await fetchPendingRef.current({ background: true })
  })

  // Tira o chat da fila na hora (sem esperar a reconciliação).
  const removePending = useCallback((chatId: number) => {
    setPendingChats((prev) => (prev.some((c) => c.id === chatId) ? prev.filter((c) => c.id !== chatId) : prev))
  }, [])

  useEffect(() => {
    const unsubscribe = onChatNew(() => {
      // Chat novo depende de filtro/ordem de fila do backend: entra pela reconciliação.
      schedulePendingRefetch()
    })
    return unsubscribe
  }, [onChatNew, schedulePendingRefetch])

  useEffect(() => {
    const unsubscribe = onChatStarted((data) => {
      removePending(Number(data?.chatId))
      schedulePendingRefetch()
    })
    return unsubscribe
  }, [onChatStarted, schedulePendingRefetch, removePending])

  useEffect(() => {
    // Pendente encerrado (cliente desistiu / automação) também sai da fila.
    const unsubscribe = onChatFinished((data) => {
      removePending(Number(data?.chatId))
    })
    return unsubscribe
  }, [onChatFinished, removePending])

  const handleStartNext = async () => {
    setIsStarting(true)
    try {
      const response = await api.post('/chats/start-next', {
        categoryIds: selectedCategories.length > 0 ? selectedCategories : undefined,
      })

      if (response.data.chatId) {
        toast({
          title: 'Atendimento iniciado',
          status: 'success',
          duration: 3000,
        })
        onStartChat?.()
        navigate(`/tickets/${response.data.chatId}`)
      } else {
        toast({
          title: 'Nenhum atendimento pendente',
          status: 'info',
          duration: 3000,
        })
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao iniciar atendimento',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsStarting(false)
      fetchPending({ background: true })
    }
  }

  const handleStartSpecificChat = async (chatId: number) => {
    setStartingChatId(chatId)
    try {
      await api.post(`/chats/${chatId}/start`)
      removePending(chatId)
      toast({
        title: 'Atendimento iniciado',
        status: 'success',
        duration: 3000,
      })
      onStartChat?.()
      navigate(`/tickets/${chatId}`)
    } catch (error: any) {
      toast({
        title: 'Erro ao iniciar atendimento',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setStartingChatId(null)
      fetchPending({ background: true })
    }
  }

  const planLabels: Record<string, string> = {
    default: 'Plano Padrão',
    isLightShopee: 'Light Shopee',
    isMariaMonteiro: 'Maria Monteiro',
    isGuilhermeCirilo: 'Guilherme Cirilo',
    isElite: 'Elite',
    isGold: 'Gold',
    isMembroBlack: 'Membro Black',
  }

  const fetchModalCategories = async (plan: string) => {
    setIsLoadingModalCategories(true)
    try {
      const response = await api.get('/chats/pending', {
        params: {
          includeChats: 'false',
          planType: plan || undefined,
        },
      })
      setModalCategories(response.data.categories || [])
    } catch (error) {
      console.error('Error fetching modal categories:', error)
    } finally {
      setIsLoadingModalCategories(false)
    }
  }

  const openFilterModal = () => {
    setTempCategories([...selectedCategories])
    setTempPlan(selectedPlan)
    fetchModalCategories(selectedPlan)
    onFilterOpen()
  }

  const handleTempPlanChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPlan = e.target.value
    setTempPlan(newPlan)
    setTempCategories([])
    fetchModalCategories(newPlan)
  }

  const applyFilters = () => {
    setSelectedCategories(tempCategories)
    setSelectedPlan(tempPlan)
    onFilterClose()
  }

  const removeCategory = (categoryId: number) => {
    setSelectedCategories((prev) => prev.filter((id) => id !== categoryId))
  }

  const removePlan = () => {
    setSelectedPlan('')
  }

  const clearAllFilters = () => {
    setSelectedCategories([])
    setSelectedPlan('')
  }

  const activeFilterCount = selectedCategories.length + (selectedPlan ? 1 : 0)
  const hasActiveFilters = activeFilterCount > 0

  if (isLoading) {
    return (
      <Flex justify="center" p={8}>
        <Spinner size="lg" color="yellow.500" />
      </Flex>
    )
  }

  return (
    <VStack spacing={0} align="stretch" h="100%" overflow="hidden">
      <VStack spacing={2} align="stretch" p={{ base: 2, md: 3 }} flexShrink={0}>
        <VStack spacing={2} align="stretch" w="100%">
          <HStack justify="space-between" align="center" spacing={2} flexWrap="wrap">
            <HStack spacing={2} flex={1} minW={0}>
              <Button
                size={{ base: 'xs', md: 'sm' }}
                variant="outline"
                colorScheme={hasActiveFilters ? 'yellow' : 'gray'}
                leftIcon={
                  <Box as="span" display="inline-flex" flexShrink={0} lineHeight={1}>
                    <Funnel size={14} />
                  </Box>
                }
                onClick={openFilterModal}
                position="relative"
                flexShrink={0}
              >
                <Text display={{ base: 'none', sm: 'block' }}>Filtrar</Text>
                {hasActiveFilters && (
                  <Badge
                    ml={1}
                    bg="yellow.500"
                    color={textOnYellow}
                    borderRadius="full"
                    fontSize="10px"
                    minW="18px"
                    textAlign="center"
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button
                  size="xs"
                  variant="ghost"
                  color={textMuted}
                  _hover={{ color: textPrimary }}
                  onClick={clearAllFilters}
                  fontSize="xs"
                  flexShrink={0}
                >
                  Limpar
                </Button>
              )}
            </HStack>
            <Button
              size={{ base: 'xs', md: 'sm' }}
              leftIcon={<Play size={14} weight="fill" />}
              bg="yellow.500"
              color={textOnYellow}
              _hover={{ bg: 'yellow.400' }}
              isLoading={isStarting}
              loadingText="Iniciando..."
              onClick={handleStartNext}
              isDisabled={pendingCount === 0}
              flexShrink={0}
              sx={{
                '& .chakra-button__icon': {
                  width: '14px',
                  height: '14px',
                  minWidth: '14px',
                  maxWidth: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              }}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Próximo</Text>
            </Button>
          </HStack>

        {hasActiveFilters && (
          <Wrap spacing={1}>
            {selectedPlan && (
              <WrapItem>
                <Tag size="sm" colorScheme="yellow" borderRadius="full" variant="subtle">
                  <TagLabel>{planLabels[selectedPlan] || selectedPlan}</TagLabel>
                  <TagCloseButton onClick={removePlan} />
                </Tag>
              </WrapItem>
            )}
            {selectedCategories.map((catId) => {
              const cat = categories.find((c) => c.id === catId)
              return (
                <WrapItem key={catId}>
                  <Tag size="sm" colorScheme="gray" borderRadius="full" variant="subtle">
                    <TagLabel>{cat?.name || `Cat ${catId}`}</TagLabel>
                    <TagCloseButton onClick={() => removeCategory(catId)} />
                  </Tag>
                </WrapItem>
              )
            })}
          </Wrap>
        )}

        <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted}>
          {pendingCount} {pendingCount === 1 ? 'pendente' : 'pendentes'}
        </Text>
        </VStack>
      </VStack>

      <Modal isOpen={isFilterOpen} onClose={onFilterClose} size={{ base: 'full', sm: 'sm' }} isCentered>
        <ModalOverlay />
        <ModalContent bg={surfaceBg} borderColor={borderSubtle} borderWidth="1px" mx={{ base: 0, sm: 'auto' }}>
          <ModalHeader fontSize={{ base: 'sm', md: 'md' }} pb={2}>Filtrar Atendimentos</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2} color={textSecondary}>
                  Plano
                </Text>
                <Select
                  size="sm"
                  value={tempPlan}
                  onChange={handleTempPlanChange}
                  bg={inputBg}
                  borderColor={borderMedium}
                >
                  <option value="">Todos os Planos</option>
                  <option value="default">Plano Padrão</option>
                  <option value="isLightShopee">Light Shopee</option>
                  <option value="isMariaMonteiro">Maria Monteiro</option>
                  <option value="isGuilhermeCirilo">Guilherme Cirilo</option>
                  <option value="isElite">Elite</option>
                  <option value="isGold">Gold</option>
                  <option value="isMembroBlack">Membro Black</option>
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2} color={textSecondary}>
                  Categorias
                </Text>
                {isLoadingModalCategories ? (
                  <Flex justify="center" py={3}>
                    <Spinner size="sm" color="yellow.500" />
                  </Flex>
                ) : modalCategories.length > 0 ? (
                  <SimpleGrid columns={1} spacing={1}>
                    {modalCategories.map((cat) => (
                      <Checkbox
                        key={cat.id}
                        size="sm"
                        colorScheme="yellow"
                        isChecked={tempCategories.includes(cat.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTempCategories((prev) => [...prev, cat.id])
                          } else {
                            setTempCategories((prev) => prev.filter((id) => id !== cat.id))
                          }
                        }}
                      >
                        <HStack spacing={2}>
                          <Text fontSize="sm">{cat.name}</Text>
                          <Badge colorScheme="gray" borderRadius="full" fontSize="10px">
                            {cat.count}
                          </Badge>
                        </HStack>
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Text fontSize="xs" color={textMutedSame}>
                    Nenhuma categoria com pendentes
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button size="sm" variant="ghost" mr={2} onClick={onFilterClose}>
              Cancelar
            </Button>
            <Button
              size="sm"
              bg="yellow.500"
              color={textOnYellow}
              _hover={{ bg: 'yellow.400' }}
              onClick={applyFilters}
            >
              Aplicar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {SHOW_PENDING_CHAT_LIST && <PullToRefreshIndicator pull={pull} refreshing={refreshing} arrastando={arrastando} />}
      {SHOW_PENDING_CHAT_LIST ? (
        <VStack
          ref={pendingListRef}
          spacing={1}
          align="stretch"
          overflowY="auto"
          flex={1}
          minH={0}
          px={2}
          pb={2}
          onScroll={(e) => {
            const el = e.currentTarget
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 100 && hasMore && !isLoadingMore) {
              fetchMore()
            }
          }}
        >
          {pendingChats.length === 0 ? (
            <Flex direction="column" align="center" justify="center" py={8}>
              <Box color={iconMuted} display="inline-flex" flexShrink={0}>
                <Hourglass size={40} color="currentColor" weight="duotone" />
              </Box>
              <Text color={textMutedSame} textAlign="center" mt={3} fontSize="sm">
                Nenhum atendimento pendente
              </Text>
            </Flex>
          ) : (
            pendingChats.map((chat) => (
              <Box
                key={chat.id}
                px={{ base: 3, md: 3 }}
      py={{ base: 3, md: 3 }}
                borderRadius="lg"
                bg="transparent"
                _hover={{ bg: bgHoverDefault }}
                cursor="pointer"
                onClick={() => handleStartSpecificChat(chat.id)}
                borderBottom="1px solid"
                borderBottomColor={borderSubtle}
                flexShrink={0}
                transition="background-color 0.2s ease"
                opacity={startingChatId === chat.id ? 0.5 : 1}
                pointerEvents={startingChatId ? 'none' : 'auto'}
              >
                <HStack spacing={{ base: 2, md: 3 }} align="start">
                  <Avatar
                    size={{ base: 'md', lg: 'sm' }}
                    name={chat.ownerName}
                    src={chat.ownerProfilePic || undefined}
                    bg="yellow.500"
                    color={textOnYellow}
                    flexShrink={0}
                  />
                  <VStack flex={1} align="start" spacing={0} overflow="hidden" minW={0}>
                    <Text fontWeight="semibold" color={textPrimary} noOfLines={1} fontSize={{ base: 'xs', md: 'sm' }} w="100%">
                      {chat.ownerName}
                    </Text>
                    <HStack spacing={1} flexWrap="wrap" mt={1}>
                      <Badge
                        size="sm"
                        bg={chat.categoryColor || 'blue.500'}
                        color={textPrimary}
                        fontSize={{ base: '9px', md: '10px' }}
                        borderRadius="full"
                        px={1}
                      >
                        {chat.categoryName || 'Sem categoria'}
                      </Badge>
                      {chat.isGuest && chat.channel !== 'whatsapp' && <GuestBadge size="xs" />}
                      {chat.channel === 'whatsapp' && <WhatsappBadge size="xs" />}
                    </HStack>
                    {chat.channel === 'whatsapp' && chat.whatsappWaId && (
                      <Text fontSize="2xs" color={textMuted} noOfLines={1} mt={0.5}>
                        {formatWaId(chat.whatsappWaId)}{chat.whatsappProfileName ? ` · ${chat.whatsappProfileName}` : ''}
                      </Text>
                    )}
                    {chat.isGuest && chat.guestEmail && !isPlaceholderGuestEmail(chat.guestEmail) && (
                      <Text fontSize="2xs" color={textMuted} noOfLines={1} mt={0.5}>
                        {chat.guestEmail}
                      </Text>
                    )}
                    <Text fontSize={{ base: '2xs', md: 'xs' }} color={textMuted} noOfLines={1} mt={1}>
                      {chat.lastMessage?.message || 'Sem mensagens'}
                    </Text>
                    <HStack w="100%" justify="space-between" mt={1} spacing={1} flexWrap="wrap">
                      <Text fontSize="2xs" color={textMutedSame} noOfLines={1}>
                        {format(new Date(chat.createdAt), 'dd/MM/yy', { locale: ptBR })}
                      </Text>
                      <Text fontSize="2xs" color={textMutedSame} flexShrink={0} noOfLines={1}>
                        {formatDistanceToNow(new Date(chat.updatedAt), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </Text>
                    </HStack>
                  </VStack>
                  {startingChatId === chat.id && (
                    <Spinner size="sm" color="yellow.500" flexShrink={0} />
                  )}
                </HStack>
              </Box>
            ))
          )}
          {isLoadingMore && (
            <Flex justify="center" py={3}>
              <Spinner size="sm" color="yellow.500" />
            </Flex>
          )}
        </VStack>
      ) : (
        <Box p={8} textAlign="center">
          <VStack spacing={4}>
            <Box display="inline-flex" flexShrink={0}>
              <Hourglass size={48} color="#ECC94B" weight="duotone" />
            </Box>
            <Text fontSize="xl" fontWeight="bold" color={textPrimary}>
              {pendingCount} {pendingCount === 1 ? 'atendimento pendente' : 'atendimentos pendentes'}
            </Text>
            <Text color={textMuted}>
              Clique no botão acima para iniciar o próximo atendimento
            </Text>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}

function ChatsList({
  status,
  onSelectChat,
  selectedChatId,
}: {
  status: string
  onSelectChat: (chat: SupportChat) => void
  selectedChatId?: number
}) {
  const [chats, setChats] = useState<SupportChat[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const chatListRef = useRef<HTMLDivElement>(null)
  const processedMsgIds = useRef<Set<string>>(new Set())
  const [transferChatId, setTransferChatId] = useState<number | null>(null)
  const [agents, setAgents] = useState<{ id: number; name: string }[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')
  const [isTransferring, setIsTransferring] = useState(false)
  const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure()
  const toast = useToast()
  const navigate = useNavigate()
  const { 
    onNewMessage, 
    onChatStarted, 
    onChatFinished, 
    onMessagesRead, 
    onChatTransferred,
    onChatUpdated,
    onUnreadUpdate,
    onTypingStarted,
    onTypingStopped,
  } = useSocket()
  const { user } = useAuth()
  
  // Estado para tracking de quem está digitando
  const [typingUsers, setTypingUsers] = useState<Map<number, string>>(new Map())

  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderSubtle = useColorModeValue('gray.200', 'gray.700')
  const borderMedium = useColorModeValue('gray.300', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const textMutedSame = useColorModeValue('gray.500', 'gray.500')
  const inputBg = useColorModeValue('gray.100', 'gray.700')
  const iconMuted = useColorModeValue('gray.500', 'gray.600')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400)
    return () => clearTimeout(timer)
  }, [search])

  // Lista atual (tamanho para a reconciliação e checagens fora do updater) e
  // geração da consulta: troca de busca/aba invalida respostas em voo.
  const chatsRef = useRef<SupportChat[]>([])
  useEffect(() => {
    chatsRef.current = chats
  }, [chats])
  const listGenRef = useRef(0)

  /**
   * Sem `background`: carga inicial / busca (spinner, volta ao topo).
   * Com `background`: reconciliação após evento de socket — rebusca as páginas
   * já carregadas e troca a lista no lugar, sem desmontar o container de scroll
   * (o spinner de `isLoading` desmontava a lista e a rolagem voltava ao topo).
   */
  const fetchChats = useCallback(async (opts?: { background?: boolean }) => {
    const background = !!opts?.background
    const gen = background ? listGenRef.current : ++listGenRef.current
    const limit = background ? syncLimitFor(chatsRef.current.length) : PAGE_SIZE
    if (!background) {
      setIsLoading(true)
      setPage(1)
      setHasMore(true)
    }
    try {
      const response = await api.get('/chats/my-chats', {
        params: {
          status,
          search: debouncedSearch || undefined,
          page: 1,
          limit,
        },
      })
      if (gen !== listGenRef.current) return // busca/aba mudou no meio do caminho
      const fresh: SupportChat[] = response.data.data || []
      setChats(fresh)
      setPage(Math.max(1, Math.ceil(fresh.length / PAGE_SIZE)))
      const meta = response.data.meta
      setHasMore(meta ? fresh.length < Number(meta.total) : fresh.length >= limit)
    } catch (error) {
      console.error('Error fetching chats:', error)
    } finally {
      if (!background) setIsLoading(false)
    }
  }, [status, debouncedSearch])

  const fetchMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return
    setIsLoadingMore(true)
    const gen = listGenRef.current
    const nextPage = page + 1
    try {
      const response = await api.get('/chats/my-chats', {
        params: {
          status,
          search: debouncedSearch || undefined,
          page: nextPage,
          limit: PAGE_SIZE,
        },
      })
      if (gen !== listGenRef.current) return
      const newChats = response.data.data
      if (newChats.length > 0) {
        setChats((prev) => appendUnique(prev, newChats))
        setPage(nextPage)
      }
      const meta = response.data.meta
      if (meta && nextPage >= meta.lastPage) {
        setHasMore(false)
      }
      if (newChats.length === 0) {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching more chats:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [status, debouncedSearch, page, isLoadingMore, hasMore])

  useEffect(() => {
    fetchChats()
  }, [fetchChats])

  // Mesmo padrão do PendingChatsTab: agrupa rajadas de eventos socket num
  // único refetch em vez de uma query por evento por agente conectado.
  const fetchChatsRef = useRef(fetchChats)
  useEffect(() => {
    fetchChatsRef.current = fetchChats
  }, [fetchChats])
  const chatsRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleChatsRefetch = useCallback(() => {
    if (chatsRefetchTimer.current) return
    chatsRefetchTimer.current = setTimeout(() => {
      chatsRefetchTimer.current = null
      fetchChatsRef.current({ background: true })
    }, 2500)
  }, [])

  // App voltou do segundo plano / socket reconectou: busca de novo (sem spinner).
  useEffect(
    () =>
      onResync((origem) => {
        if (origem !== 'meus-chats') fetchChatsRef.current({ background: true })
      }),
    []
  )

  const { pull, refreshing, arrastando } = usePullToRefresh(chatListRef, async () => {
    dispararResync('meus-chats')
    await fetchChatsRef.current({ background: true })
  })

  // Remoção imediata (encerrado / transferido / assumido por outro).
  const removeChat = useCallback((chatId: number) => {
    setChats((prev) => (prev.some((c) => c.id === chatId) ? prev.filter((c) => c.id !== chatId) : prev))
  }, [])
  const hasChat = useCallback((chatId: number) => chatsRef.current.some((c) => c.id === chatId), [])
  // Refs para os handlers de socket não lerem valor velho (o effect não
  // re-assina a cada troca de chat selecionado).
  const myIdRef = useRef<number | null>(user?.id ?? null)
  myIdRef.current = user?.id ?? null
  const selectedChatIdRef = useRef<number | undefined>(selectedChatId)
  selectedChatIdRef.current = selectedChatId
  useEffect(
    () => () => {
      if (chatsRefetchTimer.current) clearTimeout(chatsRefetchTimer.current)
    },
    []
  )

  // Listen for new messages to update list
  useEffect(() => {
    const unsubscribeMessage = onNewMessage((data) => {
      const msgKey = `${data.chatId}-${data.id || data.createdAt}`
      if (processedMsgIds.current.has(msgKey)) return
      processedMsgIds.current.add(msgKey)
      if (processedMsgIds.current.size > 500) {
        const entries = Array.from(processedMsgIds.current)
        processedMsgIds.current = new Set(entries.slice(-250))
      }

      // chatId chega como string em parte dos emits (params.id): normaliza.
      const chatId = Number(data.chatId)
      if (!hasChat(chatId)) {
        // Chat ainda não está na lista (recém-atribuído/transferido): reconcilia.
        scheduleChatsRefetch()
        return
      }
      setChats((prev) => {
        const index = prev.findIndex((c) => c.id === chatId)
        if (index > -1) {
          const updated = [...prev]
          const isCurrentlyViewing = chatId === selectedChatIdRef.current
          const newUnreadCount = data.senderType === 'user' && !isCurrentlyViewing
            ? (updated[index].unreadCountAgent || 0) + 1
            : updated[index].unreadCountAgent
          
          updated[index] = {
            ...updated[index],
            updatedAt: data.createdAt || new Date().toISOString(),
            lastMessage: {
              message: data.messageType === 'text' ? data.message : `[${data.messageType}]`,
              createdAt: data.createdAt || new Date().toISOString(),
              senderType: data.senderType,
            },
            unreadCountAgent: newUnreadCount,
          }
          // Reordena no lugar (transferidos por último, não lidos primeiro, id asc)
          return sortMyChats(updated)
        }
        return prev
      })
    })

    // Listen for messages read to reset unread count
    const unsubscribeRead = onMessagesRead((data) => {
      const chatId = Number(data.chatId)
      setChats((prev) => {
        const index = prev.findIndex((c) => c.id === chatId)
        if (index > -1) {
          const updated = [...prev]
          updated[index] = {
            ...updated[index],
            unreadCountAgent: 0,
            transferredAt: null,
          }
          return updated
        }
        return prev
      })
    })

    // Mudanças de estado: o item afetado sai/entra NA HORA quando o payload
    // basta; inserção (precisa da linha completa do backend) vai pela
    // reconciliação em segundo plano, que preserva a rolagem.
    const unsubscribeStarted = onChatStarted((data) => {
      const chatId = Number(data?.chatId)
      const agentId = data?.agentId != null ? Number(data.agentId) : null
      if (agentId != null && agentId !== myIdRef.current) {
        // Assumido por outro atendente (ou bot): não é mais meu
        removeChat(chatId)
        return
      }
      if (status === 'in_progress' && !hasChat(chatId)) scheduleChatsRefetch()
    })
    const unsubscribeFinished = onChatFinished((data) => {
      removeChat(Number(data?.chatId))
    })
    const unsubscribeTransferred = onChatTransferred((data) => {
      const chatId = Number(data?.chatId)
      const toAgentId = data?.toAgentId != null ? Number(data.toAgentId) : null
      if (toAgentId === myIdRef.current) {
        // Veio para mim: aparece em "Transferidos" (sai de "Andamento" se estava lá)
        if (status === 'transferred') {
          if (!hasChat(chatId)) scheduleChatsRefetch()
        } else {
          removeChat(chatId)
        }
        return
      }
      // Saiu de mim (ou destino desconhecido): remove e reconcilia
      removeChat(chatId)
      if (toAgentId == null) scheduleChatsRefetch()
    })
    // Visitante vinculado a uma conta: nome/e-mail da linha mudam.
    // chat:updated também chega a cada mensagem (hasNewMessage) — só o vínculo interessa aqui.
    const unsubscribeUpdated = onChatUpdated((data) => {
      if (data?.action === 'guest_linked') scheduleChatsRefetch()
    })
    
    // Listen for unread count updates
    const unsubscribeUnread = onUnreadUpdate((data) => {
      const chatId = Number(data.chatId)
      setChats((prev) => {
        const index = prev.findIndex((c) => c.id === chatId)
        if (index > -1) {
          const updated = [...prev]
          updated[index] = {
            ...updated[index],
            unreadCountAgent: data.unreadCountAgent,
          }
          return sortMyChats(updated)
        }
        return prev
      })
    })
    
    // Listen for typing indicators. Cada "digitando" tem validade curta: se o
    // typing:stopped se perder (aba fechada, socket caiu), a linha não fica
    // "digitando..." por horas.
    const typingExpiry = new Map<number, ReturnType<typeof setTimeout>>()
    const limparTyping = (chatId: number) => {
      const t = typingExpiry.get(chatId)
      if (t) clearTimeout(t)
      typingExpiry.delete(chatId)
      setTypingUsers((prev) => {
        if (!prev.has(chatId)) return prev
        const newMap = new Map(prev)
        newMap.delete(chatId)
        return newMap
      })
    }

    const unsubscribeTypingStart = onTypingStarted((data) => {
      if (data.senderType === 'user') {
        const id = Number(data.chatId)
        setTypingUsers((prev) => {
          const newMap = new Map(prev)
          newMap.set(id, data.userName || 'Usuário')
          return newMap
        })
        const anterior = typingExpiry.get(id)
        if (anterior) clearTimeout(anterior)
        typingExpiry.set(id, setTimeout(() => limparTyping(id), 12_000))
      }
    })

    const unsubscribeTypingStop = onTypingStopped((data) => {
      limparTyping(Number(data.chatId))
    })

    return () => {
      unsubscribeMessage()
      unsubscribeRead()
      unsubscribeStarted()
      unsubscribeFinished()
      unsubscribeTransferred()
      unsubscribeUpdated()
      unsubscribeUnread()
      unsubscribeTypingStart()
      unsubscribeTypingStop()
      typingExpiry.forEach((t) => clearTimeout(t))
    }
  }, [status, onNewMessage, onMessagesRead, onChatStarted, onChatFinished, onChatTransferred, onChatUpdated, onUnreadUpdate, onTypingStarted, onTypingStopped, scheduleChatsRefetch, removeChat, hasChat])

  // Fetch agents for transfer modal
  const fetchAgents = useCallback(async () => {
    try {
      const response = await api.get('/agents')
      setAgents(response.data.data || response.data)
    } catch (error) {
      console.error('Error fetching agents:', error)
    }
  }, [])

  const handleMarkUnread = async (chatId: number) => {
    try {
      await api.post(`/chats/${chatId}/mark-unread`)
      setChats((prev) =>
        prev.map((chat) =>
          chat.id === chatId
            ? { ...chat, unreadCountAgent: chat.unreadCountAgent > 0 ? chat.unreadCountAgent : 1 }
            : chat
        )
      )
      toast({
        title: 'Marcado como não lido',
        status: 'success',
        duration: 2000,
      })
    } catch (error: any) {
      toast({
        title: 'Erro ao marcar como não lido',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 3000,
      })
    }
  }

  const handleOpenTransfer = (chatId: number) => {
    setTransferChatId(chatId)
    setSelectedAgentId('')
    fetchAgents()
    onTransferOpen()
  }

  const handleTransfer = async () => {
    if (!transferChatId || !selectedAgentId) return
    setIsTransferring(true)
    try {
      await api.post(`/chats/${transferChatId}/transfer`, {
        toAgentId: Number(selectedAgentId),
      })
      toast({
        title: 'Atendimento transferido',
        status: 'success',
        duration: 3000,
      })
      onTransferClose()
      removeChat(transferChatId)
      navigate('/tickets')
    } catch (error: any) {
      toast({
        title: 'Erro ao transferir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsTransferring(false)
    }
  }

  const handleFinish = async (chatId: number) => {
    try {
      await api.post(`/chats/${chatId}/finish`)
      toast({
        title: 'Atendimento concluído',
        status: 'success',
        duration: 3000,
      })
      removeChat(chatId)
      navigate('/tickets')
    } catch (error: any) {
      toast({
        title: 'Erro ao concluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  if (isLoading) {
    return (
      <Flex justify="center" p={8}>
        <Spinner size="lg" color="yellow.500" />
      </Flex>
    )
  }

  return (
    <>
      <VStack spacing={2} align="stretch" h="100%" overflow="hidden">
        <Box px={{ base: 1, md: 2 }} pt={2} flexShrink={0}>
          <InputGroup size={{ base: 'xs', md: 'sm' }}>
            <InputLeftElement>
              <Box as="span" color={iconMuted} display="inline-flex">
                <MagnifyingGlass size={16} color="currentColor" />
              </Box>
            </InputLeftElement>
            <Input
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outline"
              fontSize={{ base: 'xs', md: 'sm' }}
            />
          </InputGroup>
        </Box>
        <PullToRefreshIndicator pull={pull} refreshing={refreshing} arrastando={arrastando} />
        <VStack
          ref={chatListRef}
          spacing={1}
          align="stretch"
          overflowY="auto"
          flex={1}
          minH={0}
          px={2}
          pb={2}
          onScroll={(e) => {
            const el = e.currentTarget
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 100 && hasMore && !isLoadingMore) {
              fetchMore()
            }
          }}
        >
          {chats.length === 0 ? (
            <Text color={textMutedSame} textAlign="center" py={8}>
              Nenhum atendimento {status === 'in_progress' ? 'em andamento' : status === 'transferred' ? 'transferido' : status}
            </Text>
          ) : (
            chats.map((chat) => (
              <ChatItem
                key={chat.id}
                chat={chat}
                isSelected={chat.id === selectedChatId}
                onClick={() => onSelectChat(chat)}
                showMenu={status === 'in_progress'}
                onMarkUnread={handleMarkUnread}
                onTransfer={handleOpenTransfer}
                onFinish={handleFinish}
                isTyping={typingUsers.has(chat.id)}
              />
            ))
          )}
          {isLoadingMore && (
            <Flex justify="center" py={3}>
              <Spinner size="sm" color="yellow.500" />
            </Flex>
          )}
        </VStack>
      </VStack>

      {/* Transfer Modal */}
      <Modal isOpen={isTransferOpen} onClose={onTransferClose} isCentered size={{ base: 'full', sm: 'md' }}>
        <ModalOverlay />
        <ModalContent bg={surfaceBg} borderColor={borderSubtle} mx={{ base: 0, sm: 'auto' }}>
          <ModalHeader color={textPrimary} fontSize={{ base: 'sm', md: 'md' }}>Transferir Atendimento</ModalHeader>
          <ModalCloseButton color={textMuted} />
          <ModalBody>
            <Text color={textMuted} mb={3}>
              Selecione o atendente para transferir o atendimento:
            </Text>
            <Select
              placeholder="Selecione um atendente"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              variant="outline"
              bg={inputBg}
              borderColor={borderMedium}
              _hover={{ borderColor: borderMedium }}
              color={textPrimary}
            >
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onTransferClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleTransfer}
              isLoading={isTransferring}
              isDisabled={!selectedAgentId}
            >
              Transferir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default function Tickets() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [selectedChat, setSelectedChat] = useState<SupportChat | null>(null)
  // No celular o painel do cliente é um drawer: começa fechado e abre só ao tocar.
  const [showUserInfo, setShowUserInfo] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 62em)').matches,
  )
  const [activeTabIndex, setActiveTabIndex] = useState(0) // 0=Pendentes, 1=Andamento, 2=Transferidos
  const [pendingCount, setPendingCount] = useState(0)
  const [inProgressUnread, setInProgressUnread] = useState(0)
  const [transferredUnread, setTransferredUnread] = useState(0)
  const [internalTicketsBadge, setInternalTicketsBadge] = useState(0)
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  const { onNewMessage, onChatNew, onChatStarted, onChatFinished, onChatTransferred, onUnreadUpdate } = useSocket()

  const surfaceBg = useColorModeValue('white', 'gray.800')
  const pageBg = useColorModeValue('gray.50', 'gray.900')
  const borderSubtle = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMutedSame = useColorModeValue('gray.500', 'gray.500')
  const iconMuted = useColorModeValue('gray.500', 'gray.600')
  const menuIconColor = useColorModeValue('gray.700', 'white')

  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await api.get('/chats/pending')
      setPendingCount(response.data.count)
    } catch (error) {
      console.error('Error fetching pending count:', error)
    }
  }, [])

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const response = await api.get('/chats/unread-counts')
      setInProgressUnread(response.data.inProgress)
      setTransferredUnread(response.data.transferred)
    } catch (error) {
      console.error('Error fetching unread counts:', error)
    }
  }, [])

  const fetchInternalTicketsCount = useCallback(async () => {
    try {
      const res = await api.get('/internal-tickets/my-pending-count')
      setInternalTicketsBadge(res.data.count || 0)
    } catch {
      // ignore
    }
  }, [])

  // Buscar contagens ao montar + polling a cada 30s como fallback
  useEffect(() => {
    fetchPendingCount()
    fetchUnreadCounts()
    fetchInternalTicketsCount()
    const interval = setInterval(() => {
      fetchPendingCount()
      fetchUnreadCounts()
      fetchInternalTicketsCount()
    }, 120000)

    const handleNew = () => fetchInternalTicketsCount()
    const handleReply = () => fetchInternalTicketsCount()
    const handleResolved = () => fetchInternalTicketsCount()

    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
    socketService.on(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)

    return () => {
      clearInterval(interval)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.NEW, handleNew)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.REPLY, handleReply)
      socketService.off(SOCKET_EVENTS.INTERNAL_TICKET.RESOLVED, handleResolved)
    }
  }, [fetchPendingCount, fetchUnreadCounts, fetchInternalTicketsCount])

  // Voltou do segundo plano / puxou para atualizar: os badges também.
  useEffect(
    () =>
      onResync(() => {
        fetchPendingCount()
        fetchUnreadCounts()
        fetchInternalTicketsCount()
      }),
    [fetchPendingCount, fetchUnreadCounts, fetchInternalTicketsCount]
  )

  // Escutar eventos de socket que afetam as contagens
  useEffect(() => {
    const unsubs = [
      onChatNew(() => { fetchPendingCount(); fetchUnreadCounts() }),
      onChatStarted(() => { fetchPendingCount(); fetchUnreadCounts() }),
      onChatFinished(() => { fetchPendingCount(); fetchUnreadCounts() }),
      onChatTransferred(() => { fetchPendingCount(); fetchUnreadCounts() }),
      onUnreadUpdate(() => fetchUnreadCounts()),
    ]
    return () => unsubs.forEach((unsub) => unsub())
  }, [onChatNew, onChatStarted, onChatFinished, onChatTransferred, onUnreadUpdate, fetchPendingCount, fetchUnreadCounts])

  const handleSelectChat = (chat: SupportChat) => {
    setSelectedChat(chat)
    navigate(`/tickets/${chat.id}`)
  }

  const handleBack = () => {
    setSelectedChat(null)
    navigate('/tickets')
  }

  const handleChatFinished = async () => {
    try {
      const res = await api.get('/chats/my-chats', { params: { status: 'in_progress', limit: 10 } })
      const nextChats = res.data.data || res.data || []
      const next = nextChats.find((c: any) => c.id !== selectedChat?.id)
      if (next) {
        setSelectedChat(next)
        setActiveTabIndex(1)
        navigate(`/tickets/${next.id}`)
      } else {
        setSelectedChat(null)
        setActiveTabIndex(1)
        navigate('/tickets')
      }
    } catch {
      setSelectedChat(null)
      navigate('/tickets')
    }
    fetchPendingCount()
  }

  // Load chat if ID is in URL
  useEffect(() => {
    if (id) {
      api.get(`/chats/${id}`).then((response) => {
        setSelectedChat(response.data)
        if (response.data.status === 'in_progress') {
          setActiveTabIndex(1)
        }
      }).catch((error) => {
        if (error.response?.status === 403) {
          toast({
            title: 'Acesso negado',
            description: 'Você não tem permissão para ver este atendimento',
            status: 'error',
            duration: 5000,
          })
        }
        navigate('/tickets')
      })
    }
  }, [id, navigate, toast])

  // Quando receber nova mensagem de usuário, atualizar contadores
  useEffect(() => {
    const unsubscribe = onNewMessage((data) => {
      if (data.senderType === 'user') {
        fetchUnreadCounts()
      }
    })
    return unsubscribe
  }, [onNewMessage, fetchUnreadCounts])

  const showSidebar = !isMobile || !selectedChat

  return (
    <>
      {/* Mobile menu drawer */}
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} internalTicketsBadge={internalTicketsBadge} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Flex h="100%" overflow="hidden" direction={{ base: 'column', lg: 'row' }}>
        {/* Chats sidebar */}
      {showSidebar && (
        <Box
          w={{ base: '100%', lg: '350px' }}
          minW={{ base: '100%', lg: '300px', xl: '350px' }}
          maxW={{ base: '100%', lg: '400px' }}
          bg={surfaceBg}
          borderRight={{ base: 'none', lg: '1px' }}
          borderBottom={{ base: '1px', lg: 'none' }}
          borderColor={borderSubtle}
          overflow="hidden"
          // Sem altura fixa, no celular (coluna) a lista crescia com o conteúdo e
          // era cortada pelo overflow do pai: nunca aparecia rolagem.
          h="100%"
          minH={0}
          flexShrink={0}
        >
          <VStack h="100%" spacing={0}>
            <Box
              w="100%"
              p={{ base: 3, md: 4 }}
              borderBottom="1px"
              borderColor={borderSubtle}
            >
              <HStack spacing={3} align="center">
                {isMobile && (
                  <IconButton
                    aria-label="Menu"
                    icon={<List size={20} />}
                    variant="ghost"
                    onClick={onMenuOpen}
                    size="sm"
                    color={menuIconColor}
                    _hover={{ bg: borderSubtle }}
                    flexShrink={0}
                  />
                )}
                <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={textPrimary}>
                  Atendimentos
                </Text>
              </HStack>
            </Box>
            <Tabs 
              w="100%" 
              flex={1} 
              display="flex" 
              flexDirection="column" 
              variant="soft-rounded" 
              colorScheme="yellow" 
              size="xs"
              overflow="hidden"
              index={activeTabIndex}
              onChange={(index) => setActiveTabIndex(index)}
            >
              <TabList px={{ base: 2, md: 2 }} pt={3} gap={1} flexWrap="nowrap" justifyContent={{ base: 'stretch', lg: 'center' }} overflowX="auto" overflowY="visible" position="relative" zIndex={1}>
                <Tab 
                  px={{ base: 1, sm: 2 }} 
                  py={{ base: 2, lg: 1 }} 
                  fontSize="xs" 
                  position="relative" 
                  flex={{ base: 1, lg: '0 0 auto' }}
                  flexShrink={0}
                  overflow="visible"
                >
                  <HStack spacing={1} align="center">
                    <Hourglass size={14} />
                    <Text noOfLines={1}>Pendentes</Text>
                  </HStack>
                  {pendingCount > 0 && (
                    <Flex
                      position="absolute"
                      top="-6px"
                      right="-6px"
                      bg="red.500"
                      color={textPrimary}
                      borderRadius="full"
                      minW="18px"
                      h="18px"
                      fontSize="10px"
                      fontWeight="bold"
                      align="center"
                      justify="center"
                      px="4px"
                      lineHeight="1"
                      zIndex={2}
                    >
                      {pendingCount > 99 ? '99+' : pendingCount}
                    </Flex>
                  )}
                </Tab>
                <Tab 
                  px={{ base: 1, sm: 2 }} 
                  py={{ base: 2, lg: 1 }} 
                  fontSize="xs" 
                  position="relative" 
                  flex={{ base: 1, lg: '0 0 auto' }}
                  flexShrink={0}
                  overflow="visible"
                >
                  <HStack spacing={1} align="center">
                    <Play size={14} />
                    <Text noOfLines={1}>Andamento</Text>
                  </HStack>
                  {inProgressUnread > 0 && (
                    <Flex
                      position="absolute"
                      top="-6px"
                      right="-6px"
                      bg="red.500"
                      color={textPrimary}
                      borderRadius="full"
                      minW="18px"
                      h="18px"
                      fontSize="10px"
                      fontWeight="bold"
                      align="center"
                      justify="center"
                      px="4px"
                      lineHeight="1"
                      zIndex={2}
                    >
                      {inProgressUnread > 99 ? '99+' : inProgressUnread}
                    </Flex>
                  )}
                </Tab>
                <Tab 
                  px={{ base: 1, sm: 2 }} 
                  py={{ base: 2, lg: 1 }} 
                  fontSize="xs" 
                  position="relative" 
                  flex={{ base: 1, lg: '0 0 auto' }}
                  flexShrink={0}
                  overflow="visible"
                >
                  <HStack spacing={1} align="center">
                    <ArrowsLeftRight size={14} />
                    <Text noOfLines={1}>Transferidos</Text>
                  </HStack>
                  {transferredUnread > 0 && (
                    <Flex
                      position="absolute"
                      top="-6px"
                      right="-6px"
                      bg="red.500"
                      color={textPrimary}
                      borderRadius="full"
                      minW="18px"
                      h="18px"
                      fontSize="10px"
                      fontWeight="bold"
                      align="center"
                      justify="center"
                      px="4px"
                      lineHeight="1"
                      zIndex={2}
                    >
                      {transferredUnread > 99 ? '99+' : transferredUnread}
                    </Flex>
                  )}
                </Tab>
              </TabList>
              <TabPanels flex={1} overflow="hidden" mt={3} minH={0}>
                <TabPanel h="100%" p={0} overflow="hidden">
                  <PendingChatsTab onStartChat={() => setActiveTabIndex(1)} />
                </TabPanel>
                <TabPanel h="100%" p={0} overflow="hidden">
                  <ChatsList
                    status="in_progress"
                    onSelectChat={handleSelectChat}
                    selectedChatId={selectedChat?.id}
                  />
                </TabPanel>
                <TabPanel h="100%" p={0} overflow="hidden">
                  <ChatsList
                    status="transferred"
                    onSelectChat={handleSelectChat}
                    selectedChatId={selectedChat?.id}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </VStack>
        </Box>
      )}

      {/* Chat area */}
      {selectedChat ? (
        <Flex flex={1} overflow="hidden" direction={{ base: 'column', lg: 'row' }}>
          <Flex flex={1} overflow="hidden" minW={0} direction="column">
            <Box flex={1} minH={0} overflow="hidden">
              <Chat
                chatId={selectedChat.id}
                onToggleUserInfo={() => setShowUserInfo(!showUserInfo)}
                onChatFinished={handleChatFinished}
                onClose={handleBack}
              />
            </Box>
          </Flex>
          {showUserInfo && selectedChat && (
            <UserInfoSidebar
              chatId={selectedChat.id}
              userId={selectedChat.userId}
              isGuest={!!selectedChat.isGuest}
              guestLinkedUserId={selectedChat.guestLinkedUserId ?? null}
              onGuestLinked={(linkedId) =>
                setSelectedChat((prev) =>
                  prev && prev.id === selectedChat.id && prev.guestLinkedUserId !== linkedId
                    ? { ...prev, userId: linkedId, guestLinkedUserId: linkedId }
                    : prev
                )
              }
              onClose={() => setShowUserInfo(false)}
              isOpen={showUserInfo}
            />
          )}
        </Flex>
      ) : (
        !isMobile && (
          <Flex flex={1} align="center" justify="center" bg={pageBg} minW={0}>
            <VStack spacing={4} px={4}>
              <Box color={iconMuted}>
                <ChatTeardropDots size={64} color="currentColor" weight="duotone" />
              </Box>
              <Text color={textMutedSame} fontSize="lg" textAlign="center">
                Selecione um atendimento para começar
              </Text>
            </VStack>
          </Flex>
        )
      )}
    </Flex>
    </>
  )
}
